import { BILLSBY_CONFIG } from '../../config/billsby';

/**
 * Initialize Billsby configuration by setting the values in the window object
 * This ensures the static HTML file can access the environment variables
 * that are processed by React's build system
 */
const initializeBillsbyConfig = () => {
  try {
    // Set the config values in the window object for the scripts in index.html
    if (window.billsbyConfig) {
      window.billsbyConfig.companyId = BILLSBY_CONFIG.companyId;
      window.billsbyConfig.apiKey = BILLSBY_CONFIG.apiKey;
      window.billsbyConfig.environment = BILLSBY_CONFIG.environment;

      console.log('Billsby config initialized with React environment variables');
    } else {
      console.error('window.billsbyConfig not found, the Billsby script may not be loaded correctly');
    }

    // Create a single global promise for Billsby initialization if not already present
    if (!window.billsbyReady) {
      window.billsbyReady = new Promise((resolve, reject) => {
        window.billsbyResolve = resolve;
        window.billsbyReject = reject;
      });
    }

    // If the Billsby lib is already loaded, initialize it now
    if (typeof window.Billsby !== 'undefined' && typeof window.Billsby.initialize === 'function') {
      try {
        if (!window.Billsby.isInitialized) {
          window.Billsby.initialize({
            companyId: BILLSBY_CONFIG.companyId,
            apiKey: BILLSBY_CONFIG.apiKey,
            environment: BILLSBY_CONFIG.environment
          });
          console.log('Billsby initialized in React app');
        } else {
          console.log('Billsby already initialized');
        }

        // Resolve the promise for components waiting on Billsby
        if (window.billsbyResolve && !window.billsbyInitialized) {
          window.billsbyInitialized = true;
          window.billsbyResolve(true);
        }
      } catch (err) {
        console.error('Error initializing Billsby instance:', err);
      }
    } else {
      console.log('Billsby not available yet, adding script');

      // Add Billsby checkout script if not already present
      if (!document.getElementById('billsby-checkout-script')) {
        const script = document.createElement('script');
        script.id = 'billsby-checkout-script';
        script.src = 'https://js.billsby.com/v2/checkout.js';
        script.async = true;
        script.onload = () => {
          console.log('Billsby script loaded successfully');
          window.billsbyReady = new Promise((resolve) => {
            if (window.billsbyTokens) {
              resolve();
            } else {
              const checkBillsby = setInterval(() => {
                if (window.billsbyTokens) {
                  clearInterval(checkBillsby);
                  resolve();
                }
              }, 100);

              // Add timeout to prevent hanging
              setTimeout(() => {
                clearInterval(checkBillsby);
                console.warn('Billsby script loaded but tokens not available within timeout');
                resolve(null);
              }, 5000);
            }
          });
        };
        script.onerror = (error) => {
          console.warn('Failed to load Billsby script - this is expected in development if Billsby CDN is not accessible:', error);
          // Create a resolved promise instead of rejecting to prevent blocking the app
          window.billsbyReady = Promise.resolve(null);
          initializationInProgress = false;
        };
        document.body.appendChild(script);
      }

      // Set up a listener for when Billsby becomes available
      const checkInterval = setInterval(() => {
        if (typeof window.Billsby !== 'undefined' && typeof window.Billsby.initialize === 'function') {
          clearInterval(checkInterval);

          try {
            if (!window.Billsby.isInitialized) {
              window.Billsby.initialize({
                companyId: BILLSBY_CONFIG.companyId,
                apiKey: BILLSBY_CONFIG.apiKey,
                environment: BILLSBY_CONFIG.environment
              });
              console.log('Billsby initialized after delay');
            } else {
              console.log('Billsby already initialized, found after delay');
            }

            // Resolve the promise for components waiting on Billsby
            if (window.billsbyResolve && !window.billsbyInitialized) {
              window.billsbyInitialized = true;
              window.billsbyResolve(true);
            }
          } catch (err) {
            console.error('Error initializing Billsby after interval:', err);
            if (window.billsbyReject) {
              window.billsbyReject(err);
            }
          }
        }
      }, 100);

      // Clear the interval after 8 seconds to prevent memory leaks
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.billsbyInitialized) {
          console.warn('Timed out waiting for Billsby to load - this is expected in development');

          // Resolve the promise gracefully instead of rejecting to prevent app blocking
          if (window.billsbyResolve) {
            window.billsbyResolve(null);
          }
        }
      }, 8000);
    }

    // Load and initialize the tokenizer for credit card input
    loadBillsbyTokenizer();

    return true;
  } catch (error) {
    console.error('Error initializing Billsby:', error);

    // Reject the promise if there was an error
    if (window.billsbyReject) {
      window.billsbyReject(error);
    }

    return false;
  }
};

/**
 * Load the Billsby tokenizer script for credit card input
 */
const loadBillsbyTokenizer = () => {
  // If tokenizer already loaded, we're done
  if (window.billsbyTokens) {
    console.log('Billsby tokenizer already loaded');
    return;
  }

  // First check if we have an embedded script
  if (!document.getElementById('billsby-tokenizer-script') && !document.querySelector('script[src*="billsby-tokens.js"]')) {
    console.log('Adding Billsby tokenizer script');

    // Add a robust script loading with fallbacks
    const scripts = [
      {
        id: 'billsby-tokenizer-script',
        src: 'https://static.billsby.com/tokenizer/v1/billsby-tokens.js'
      },
      {
        id: 'billsby-tokenizer-script-fallback',
        src: 'https://assets.billsby.com/tokenization/v2/tokenization.min.js'
      }
    ];

    const loadScript = (index) => {
      if (index >= scripts.length) {
        console.error('All Billsby tokenizer script loading attempts failed');
        return;
      }

      const { id, src } = scripts[index];
      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;

      script.onload = () => {
        console.log(`Billsby tokenizer script loaded: ${id}`);
      };

      script.onerror = () => {
        console.error(`Failed to load Billsby tokenizer script: ${id}, trying next fallback`);
        loadScript(index + 1);
      };

      document.body.appendChild(script);
    };

    // Start with the first script
    loadScript(0);
  }
};

// Simple flag to track if initialization is in progress
let initializationInProgress = false;

/**
 * Initialize the Billsby tokenizer
 * This uses the approach from your working billsby-tokenizer.html
 */
export const initializeBillsby = () => new Promise((resolve) => {
  // If already loaded, resolve immediately
  if (window.billsbyTokens) {
    console.log('Billsby already loaded, resolving immediately');
    resolve(window.billsbyTokens);
    return;
  }

  // Prevent multiple simultaneous initialization attempts
  if (initializationInProgress) {
    console.log('Billsby initialization already in progress, waiting...');
    const checkInterval = setInterval(() => {
      if (window.billsbyTokens) {
        clearInterval(checkInterval);
        resolve(window.billsbyTokens);
      }
    }, 100);

    // Add timeout
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.billsbyTokens) {
        console.warn('Billsby failed to initialize within timeout - this is expected in development');
        resolve(null);
      }
    }, 10000);

    return;
  }

  // Set flag to prevent duplicate initialization
  initializationInProgress = true;

  // If script already exists, wait for it to initialize
  if (document.querySelector('script[src*="tokenlib.billsby.com"]')) {
    console.log('Billsby script tag already exists, waiting for it to load...');
    const checkInterval = setInterval(() => {
      if (window.billsbyTokens) {
        clearInterval(checkInterval);
        initializationInProgress = false;
        resolve(window.billsbyTokens);
      }
    }, 100);

    // Add timeout
    setTimeout(() => {
      clearInterval(checkInterval);
      initializationInProgress = false;
      if (!window.billsbyTokens) {
        console.warn('Billsby failed to initialize within timeout - this is expected in development');
        resolve(null);
      }
    }, 10000);

    return;
  }

  // Create script element to match your working HTML approach
  const script = document.createElement('script');
  script.src = 'https://tokenlib.billsby.com/tokenizer.min.js';
  script.setAttribute('data-billsby-company', 'nffs'); // This appears to be your company ID

  script.onload = () => {
    console.log('Billsby script loaded, waiting for initialization');
    const checkInterval = setInterval(() => {
      if (window.billsbyTokens) {
        clearInterval(checkInterval);
        initializationInProgress = false;
        resolve(window.billsbyTokens);
      }
    }, 100);

    // Add timeout for initialization
    setTimeout(() => {
      clearInterval(checkInterval);
      initializationInProgress = false;
      if (!window.billsbyTokens) {
        console.warn('Billsby loaded but failed to initialize - this is expected in development');
        resolve(null);
      }
    }, 5000);
  };

  script.onerror = (error) => {
    console.warn('Failed to load Billsby script - this is expected in development:', error);
    initializationInProgress = false;
    resolve(null);
  };

  document.head.appendChild(script);
});

/**
 * Set up the Billsby tokenizer on form elements
 */
export const setupTokenizer = async (numberElementId, cvvElementId) => {
  try {
    const billsbyTokens = await initializeBillsby();

    // Make sure elements exist
    const numberElement = document.getElementById(numberElementId);
    const cvvElement = document.getElementById(cvvElementId);

    if (!numberElement) {
      console.error(`Credit card number element with ID "${numberElementId}" not found`);
      return { success: false, error: 'Credit card number field not found' };
    }

    if (!cvvElement) {
      console.error(`CVV element with ID "${cvvElementId}" not found`);
      return { success: false, error: 'CVV field not found' };
    }

    console.log(`Initializing Billsby with elements: ${numberElementId}, ${cvvElementId}`);

    // Initialize tokenizer
    billsbyTokens.removeHandlers();
    billsbyTokens.init(numberElementId, cvvElementId);

    return { success: true };
  } catch (error) {
    console.error('Failed to setup Billsby tokenizer:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Renamed function to avoid duplicate declaration
 * This maintains backward compatibility with other parts of the app
 */
const initializeBillsbyTokenizerConfig = () => {
  try {
    // Set the config values in the window object if needed
    if (BILLSBY_CONFIG) {
      window.billsbyCompanyId = BILLSBY_CONFIG.companyId || 'nffs';
    }

    // Initialize Billsby
    initializeBillsby()
      .then(() => console.log('Billsby initialized successfully'))
      .catch(err => console.error('Billsby initialization failed:', err));

    return true;
  } catch (error) {
    console.error('Error in billsby initialization:', error);
    return false;
  }
};

// Export the renamed function as the default export
export default () => {
  initializeBillsbyConfig();
  return initializeBillsbyTokenizerConfig();
};



// WEBPACK FOOTER //
// ./src/components/Billsby/billsbyInitializer.js